import api from './api';

export const commissionsService = {
  // Get all commissions
  getAll: async (params = {}) => {
    const response = await api.get('/commissions', { params });
    return response.data;
  },

  // Get single commission
  getById: async (id) => {
    const response = await api.get(`/commissions/${id}`);
    return response.data;
  },

  // Submit new commission
  submit: async (commissionData) => {
    const response = await api.post('/commissions', commissionData);
    return response.data;
  },

  // Update commission status (admin only)
  updateStatus: async (id, status, notes) => {
    const response = await api.put(`/commissions/${id}/status`, { status, admin_notes: notes });
    return response.data;
  },

  // Update commission notes only (admin only)
  updateNotes: async (id, notes) => {
    const response = await api.put(`/commissions/${id}/notes`, { admin_notes: notes });
    return response.data;
  },

  // Edit commission with audit trail (admin only)
  edit: async (id, editData) => {
    const response = await api.put(`/commissions/${id}/edit`, editData);
    return response.data;
  },

  // Get commission analytics
  getAnalytics: async (params = {}) => {
    const response = await api.get('/commissions/analytics', { params });
    return response.data;
  },

  // Export commissions
  export: async (format = 'csv', params = {}) => {
    const response = await api.get('/commissions/export', {
      params: { ...params, format },
      responseType: 'blob'
    });
    return response.data;
  },

  // Upload CSV commissions (admin only)
  upload: async (formData) => {
    const response = await api.post('/commissions/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Get CSV template
  getCsvTemplate: async () => {
    const response = await api.get('/commissions/csv-template');
    return response.data;
  },

  // Bulk update commission status (admin only)
  bulkUpdateStatus: async (commissionIds, status, notes) => {
    const response = await api.put('/commissions/bulk-update-status', {
      commissionIds,
      status,
      notes
    });
    return response.data;
  },

  // Get list of suppliers for filtering (admin only)
  getSuppliers: async () => {
    try {
      const agencyService = (await import('./agencyService')).default;
      const response = await agencyService.getSuppliers({ status: 'active', limit: 1000 });
      
      if (!response || !response.suppliers) {
        return [];
      }
      
      return response.suppliers.map(supplier => supplier.name);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      return [];
    }
  },

  // General commission export with filtering (admin only) - separate from payment export
  exportGeneral: async (params = {}) => {
    const response = await api.get('/commissions/export-general', {
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // Preview and process approved commissions for payment (admin only)
  getApprovedPaymentSummary: async () => {
    const response = await api.get('/commissions/export-approved-for-payment/summary');
    return response.data;
  },

  exportApprovedForPayment: async () => {
    const response = await api.post('/commissions/export-approved-for-payment', {}, {
      responseType: 'blob'
    });
    return response;
  },

  // Payment history: one record per Pay & Export
  getPaymentBatches: async () => {
    const response = await api.get('/commissions/payment-batches');
    return response.data.batches;
  },

  getPaymentBatch: async (id) => {
    const response = await api.get(`/commissions/payment-batches/${id}`);
    return response.data;
  },

  downloadPaymentBatchCsv: async (id) => {
    return api.get(`/commissions/payment-batches/${id}/csv`, { responseType: 'blob' });
  },

  // Update own commission status (independent agents only)
  updateOwnStatus: async (id, status) => {
    const response = await api.put(`/commissions/${id}/own-status`, { status });
    return response.data;
  },

  // Update draft commission (agents can only update their own drafts)
  updateDraft: async (id, draftData) => {
    const response = await api.put(`/commissions/${id}/draft`, draftData);
    return response.data;
  },

  // Submit draft commission (convert draft to pending)
  submitDraft: async (id) => {
    const response = await api.put(`/commissions/${id}/submit`);
    return response.data;
  },

  // Delete draft commission
  deleteDraft: async (id) => {
    const response = await api.delete(`/commissions/${id}`);
    return response.data;
  }
};
